import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, GlobalStyles, Snackbar, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';

import MetaTags from '../../components/MetaTags';
import { getUser } from '../../utils/cookie';

import {
  SHRED_COLORS,
  SHRED_COPY,
  SHRED_DEMO_ME_PARAM,
  SHRED_FONTS,
  SHRED_FONTS_HREF,
  SHRED_STATUS,
  SHRED_TV_PARAM,
} from './constants';
import {
  buildShareText,
  formatPct,
  getDemoMode,
  isMyEntry,
  isRookie,
  normalizeResponse,
} from './helpers';
import { useEffectiveStatus, usePrefersReducedMotion, useRankToast, useReplay, useShredData } from './hooks';
import Awards from './components/Awards';
import Board from './components/Board';
import Confetti from './components/Confetti';
import DevPanel from './DevPanel'; // DEV PANEL (temporary): remove with the line below
import CrewMeter from './components/CrewMeter';
import Explainer from './components/Explainer';
import Feed from './components/Feed';
import Hero from './components/Hero';
import MyProgress from './components/MyProgress';
import Podium from './components/Podium';
import Replay from './components/Replay';
import Shelf from './components/Shelf';
import Tv from './components/Tv';
import { StableHeight } from './components/shared';

const globalStyles = {
  '@keyframes shredEmber': {
    '0%': { transform: 'translateY(0) translateX(0)', opacity: 0 },
    '15%': { opacity: 0.9 },
    '100%': { transform: 'translateY(-420px) translateX(30px)', opacity: 0 },
  },
  '@keyframes shredPulse': {
    '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,59,48,0.5)' },
    '50%': { boxShadow: '0 0 0 10px rgba(255,59,48,0)' },
  },
  '@keyframes shredFlicker': {
    '0%, 100%': { backgroundPosition: '0 0' },
    '50%': { backgroundPosition: '0 40%' },
  },
  '@keyframes shredRise': {
    from: { opacity: 0, transform: 'translateY(32px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  '@keyframes shredHeatStripe': {
    from: { backgroundPosition: '0 0, 0 0' },
    to: { backgroundPosition: '44px 0, 0 0' },
  },
  '@keyframes shredFlame': {
    '0%, 100%': { transform: 'translateX(-50%) scale(1) rotate(-3deg)' },
    '50%': { transform: 'translateX(-50%) scale(1.18, 1.3) rotate(3deg)' },
  },
  '@keyframes shredVaultShake': {
    '0%, 100%': { transform: 'translateX(0) rotate(0)' },
    '15%': { transform: 'translateX(-9px) rotate(-3deg)' },
    '30%': { transform: 'translateX(9px) rotate(3deg)' },
    '45%': { transform: 'translateX(-7px) rotate(-2deg)' },
    '60%': { transform: 'translateX(7px) rotate(2deg)' },
    '80%': { transform: 'translateX(-3px)' },
  },
  '@keyframes shredVaultHide': {
    from: { opacity: 1 },
    to: { opacity: 0, visibility: 'hidden' },
  },
  '@keyframes shredVaultPop': {
    '0%': { opacity: 0, transform: 'scale(0.5) rotate(-8deg)' },
    '60%': { opacity: 1, transform: 'scale(1.08) rotate(2deg)' },
    '100%': { opacity: 1, transform: 'scale(1) rotate(0)' },
  },
  '@keyframes shredPrizeDrop': {
    '0%': { opacity: 0, transform: 'translate(-50%, 160%) scale(0.85)' },
    '10%': { opacity: 1, transform: 'translate(-50%, -6%) scale(1.05)' },
    '16%': { transform: 'translate(-50%, 0) scale(1)' },
    '88%': { opacity: 1, transform: 'translate(-50%, 0) scale(1)' },
    '100%': { opacity: 0, transform: 'translate(-50%, 160%) scale(0.9)' },
  },
  '@keyframes shredTwinkle': {
    '0%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
    '50%': { opacity: 1, transform: 'scale(1.25)' },
  },
  '@keyframes shredConfetti': {
    '0%': { transform: 'translate3d(0, 0, 0) rotate(0)', opacity: 1 },
    '100%': { transform: 'translate3d(var(--drift), 105vh, 0) rotate(var(--spin))', opacity: 0.9 },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.shred-motion': { animation: 'none !important', transition: 'none !important' },
    '.shred-vault-lock': { display: 'none !important' },
    '.shred-vault-reveal': { opacity: '1 !important' },
  },
};

const getDemoMe = (search) => {
  try {
    return new URLSearchParams(search).get(SHRED_DEMO_ME_PARAM);
  } catch (e) {
    return null;
  }
};

const MIN_REPLAY_DAYS = 2;

const Shred = () => {
  const { search } = useLocation();
  const demo = getDemoMode(search);
  const demoMe = getDemoMe(search);
  const tvMode = !!new URLSearchParams(search).get(SHRED_TV_PARAM);
  const userId = demo ? '' : getUser()?.userId || '';
  const reduced = usePrefersReducedMotion();

  // Day count of the live view, captured during render so the replay panel is right on first paint.
  const liveMaxDayRef = useRef(0);
  const [ready, setReady] = useState(false);
  const replay = useReplay({ maxDayRef: liveMaxDayRef, ready });
  const { data, error, isLoading, isValidating, reload } = useShredData({
    demo,
    demoMe,
    asOfDay: replay.asOfDay,
    userId,
  });

  const view = useMemo(() => data || normalizeResponse(null), [data]);
  const { challenge, entries, awards, crew, feed, me } = view;
  const finalAwardsRef = useRef(awards);
  if (!replay.asOfDay && data) {
    finalAwardsRef.current = awards;
  }
  const skewMs = data?.clockSkewMs || 0;
  const status = useEffectiveStatus(challenge, skewMs);

  // The replay range is the live view's day count, never the replayed response's.
  if (data && !replay.asOfDay) {
    liveMaxDayRef.current =
      status === SHRED_STATUS.UPCOMING ? 0 : status === SHRED_STATUS.ENDED ? challenge.dayCount : Math.max(1, challenge.currentDay);
  }
  const maxDay = liveMaxDayRef.current;
  const displayDay = replay.asOfDay ?? maxDay;

  // A failed day fetch keeps the previous day on screen; say so and stop playback instead of racing on.
  const replayError = !!error && !!replay.asOfDay;
  const pauseReplay = replay.pause;
  useEffect(() => {
    if (replayError) pauseReplay();
  }, [replayError, pauseReplay]);

  useEffect(() => {
    setReady(!!data && !isValidating);
  }, [data, isValidating]);

  const isMine = useCallback((entry) => isMyEntry(entry, me, userId), [me, userId]);

  const [notice, setNotice] = useState('');
  // Rank-change toast: live polls only, never the first load, never while a replay day is showing.
  useRankToast({ data, me, userId, replayDay: replay.asOfDay, isValidating, onNotice: setNotice });

  const [confetti, setConfetti] = useState(false);
  const celebrated = useRef(false);
  useEffect(() => {
    if (celebrated.current || !data || reduced || replay.asOfDay) return;
    const mine = me?.eligible ? me.entry : null;
    if (mine && mine.qualified && !isRookie(mine) && mine.rank <= 3) {
      celebrated.current = true;
      setConfetti(true);
    }
  }, [data, me, reduced, replay.asOfDay]);

  // React 18 has no `inert` prop, so set the property directly while the shelf is dimmed.
  const shelfRef = useRef(null);
  useEffect(() => {
    if (shelfRef.current) shelfRef.current.inert = replay.isReplaying;
  });

  const [jumpRequest, setJumpRequest] = useState(0);
  const jumpToMe = () => setJumpRequest((count) => count + 1);

  const ended = status === SHRED_STATUS.ENDED;
  const showReplay = status !== SHRED_STATUS.UPCOMING && maxDay >= MIN_REPLAY_DAYS;
  const myBracket = me?.entry?.bracket;
  const totalRanked = entries.filter((e) => e.qualified && e.bracket === myBracket).length;
  const unavailable = !!error && !data;
  const loadError = unavailable;
  const shelfFinal = ended && challenge.status === SHRED_STATUS.ENDED;
  const myEntry = me?.eligible ? me.entry : null;

  const myCard = (domId) => (
    <MyProgress domId={domId} me={me} challenge={challenge} status={status} totalRanked={totalRanked} reduced={reduced} onJump={jumpToMe} skewMs={skewMs} />
  );

  return (
    <Box sx={{ minHeight: '100vh', background: SHRED_COLORS.charcoal, color: SHRED_COLORS.text, fontFamily: SHRED_FONTS.body, overflowX: 'clip' }}>
      <MetaTags title={SHRED_COPY.PAGE_TITLE} description={SHRED_COPY.PAGE_DESCRIPTION} />
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href={SHRED_FONTS_HREF} rel="stylesheet" />
      </Helmet>
      <GlobalStyles styles={globalStyles} />
      {confetti && <Confetti onDone={() => setConfetti(false)} />}
      {tvMode && <Tv view={view} status={status} skewMs={skewMs} unavailable={unavailable} loading={isLoading && !data} />}
      <DevPanel onConfetti={() => setConfetti(true)} onNotice={setNotice} /> {/* DEV PANEL (temporary) */}

      <Hero challenge={challenge} skewMs={skewMs} unavailable={unavailable} loading={isLoading && !data} displayDay={displayDay} replaying={replay.isReplaying} onNotice={setNotice} shareText={buildShareText(SHRED_COPY.SHARE_TEXT, myEntry)} />

      <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 5 }, pb: { xs: 12, md: 8 } }}>
        {isLoading && !data && (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }} role="status" aria-live="polite">
            <CircularProgress sx={{ color: SHRED_COLORS.flame }} aria-label="Loading standings" />
          </Box>
        )}

        {(data || error) && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0,1fr)', md: 'minmax(0,1fr) 340px' }, gap: { xs: 4, md: 5 }, alignItems: 'start' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 5, md: 7 }, minWidth: 0 }}>
              {me && <Box sx={{ display: { xs: 'block', md: 'none' } }}>{myCard('shred-me-mobile')}</Box>}
              {showReplay && (
                <Replay
                  maxDay={maxDay}
                  dayCount={challenge.dayCount}
                  displayDay={displayDay}
                  errorText={replayError ? `Could not load day ${replay.asOfDay}. The board is still showing the previous day.` : ''}
                  playing={replay.playing}
                  isReplaying={replay.isReplaying}
                  loading={isValidating}
                  ended={ended}
                  onPlay={replay.play}
                  onPause={replay.pause}
                  onSeek={(day) => { replay.pause(); replay.goToDay(day); }}
                  onReset={replay.reset}
                />
              )}
              {shelfFinal && (
                <Box
                  ref={shelfRef}
                  className="shred-motion"
                  sx={{
                    opacity: replay.isReplaying ? 0.3 : 1,
                    pointerEvents: replay.isReplaying ? 'none' : 'auto',
                    transition: reduced ? 'none' : 'opacity 400ms ease',
                  }}
                >
                  <Shelf awards={finalAwardsRef.current} isMine={isMine} />
                </Box>
              )}
              {status !== SHRED_STATUS.UPCOMING && (
                <StableHeight active={replay.isReplaying}>
                  <Podium entries={entries} isMine={isMine} reduced={reduced} />
                </StableHeight>
              )}
              <StableHeight active={replay.isReplaying}>
                <Board
                  entries={entries}
                  challenge={challenge}
                  status={status}
                  me={me}
                  isMine={isMine}
                  reduced={reduced}
                  loadError={loadError}
                  onRetry={reload}
                  jumpRequest={jumpRequest}
                />
              </StableHeight>
              <Awards awards={awards} reduced={reduced} />
              <CrewMeter crew={crew} reward={challenge.rewards.crewGoal} reduced={reduced} />
              {status !== SHRED_STATUS.UPCOMING && (
                <StableHeight active={replay.isReplaying}>
                  <Feed feed={feed} />
                </StableHeight>
              )}
              <Explainer challenge={challenge} />
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 16 }}>{myCard('shred-me')}</Box>
          </Box>
        )}
      </Box>

      {myEntry && (
        <Box
          component="button"
          type="button"
          onClick={jumpToMe}
          aria-label={`Your position: ${myEntry.qualified ? `rank ${myEntry.rank}` : 'not ranked yet'}, ${formatPct(myEntry.improvementPct)}. Jump to your row.`}
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 12,
            zIndex: 1200,
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.25,
            border: `1px solid ${SHRED_COLORS.gold}66`,
            borderRadius: '999px',
            background: 'rgba(21,16,13,0.94)',
            backdropFilter: 'blur(8px)',
            color: SHRED_COLORS.text,
            font: 'inherit',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            '&:focus-visible': { outline: `2px solid ${SHRED_COLORS.gold}`, outlineOffset: 2 },
          }}
        >
          <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: 22, color: SHRED_COLORS.gold }}>
            {myEntry.qualified ? `#${myEntry.rank}` : 'Qualifying'}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 16, color: myEntry.improvementPct >= 0 ? SHRED_COLORS.positive : SHRED_COLORS.negative }}>
            {formatPct(myEntry.improvementPct)}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 13, color: SHRED_COLORS.muted }}>
            {myEntry.qualified ? `${myEntry.sessions} sessions` : `${myEntry.sessions}/${challenge.minSessions} sessions`}
          </Typography>
        </Box>
      )}

      <Snackbar
        open={!!notice}
        autoHideDuration={2600}
        onClose={() => setNotice('')}
        message={notice}
        sx={{ bottom: { xs: myEntry ? 84 : 24, md: 24 } }}
      />
    </Box>
  );
};

export default Shred;
